(ns ui.view.survey.modals.resolve-errors
  (:require
   [clojure.edn :as edn]
   [common.localization :as loc]
   [common.util :as cu]
   [re-frame.core :as rf]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.components.modal.style :as modal-style]
   [ui.components.modal.view :as modal]
   [ui.components.radix-ui.popover.view :as popover]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.components.toggle.view :as toggle]
   [ui.dom-globals :as dg]
   [ui.editors.data-input :as data-input]
   [ui.util :as uu]
   [ui.view.survey.event :as survey-view-event]
   [ui.view.survey.modals.event :as event]
   [ui.view.survey.modals.sub :as sub]
   [ui.view.survey.sub :as survey-view-sub]))

(defn resolve-cell-options
  [{:keys [options resolved-answer data-type lang]}]
  (concat
   (when-not (cu/blank-value? resolved-answer)
     [{:value nil
       :label (loc/tr :clear-select-option-label nil lang)}])
   (if (= data-type :data.type/boolean)
     [{:value true
       :label (loc/tr :yes nil lang)}
      {:value false
       :label (loc/tr :no nil lang)}]
     (->> options
          (sort-by :common/ord)
          (map (fn [opt]
                 (let [label (:label opt)]
                   (if (string? label)
                     opt
                     (assoc opt :label (cond (map? label)
                                             (or (get label lang)
                                                 (get label :ENG))))))))))))

(defn resolve-cell-value
  [{:keys [data-type resolved-options resolved-answer]}]
  (let [option-type? (some #{data-type} [:data.type/keyword :data.type/ref-single :data.type/ref-multi :data.type/boolean
                                         :data.type/keyword-multi])
        single-type? (some #{data-type} [:data.type/keyword :data.type/ref-single :data.type/boolean])]
    (cond (and option-type? single-type?)
          (->> resolved-options
               (filter (fn [opt]
                         (some #{resolved-answer} [(:value opt) (pr-str (:value opt))])))
               (first))
          option-type?
          (->> resolved-options
               (filter (fn [opt]
                         (some #{(:value opt) (pr-str (:value opt))} resolved-answer))))
          (= data-type :data.type/date)
          (if (string? resolved-answer)
            (edn/read-string resolved-answer)
            resolved-answer)
          :else
          resolved-answer)))

(defn error-cell
  [{:keys [survey-id cell-id answer-in-excel]}]
  [:div
   {:class "flex w-full px-[0.5rem] pr-[0.5rem] py-2.5 bg-danger-light cursor-pointer"
    :on-click (fn [e]
                (dg/prevent-and-stop e)
                (rf/dispatch [::survey-view-event/start-error-cell-edit survey-id cell-id]))}
   answer-in-excel])

(defn question-label-cell
  [{:keys [row-idx question-label question-ext-id]}]
  [tooltip/as-child
   {}
   (fn [f-props]
     [:td
      (merge (dissoc f-props :className)
             {:class (uu/cm "sticky left-0 z-10 bg-white min-w-[230px] max-w-[250px] p-2 border
                             border-[#D9D9DA] border-r-1 border-r-black font-bold text-xs align-middle
                             overflow-x-hidden truncate"
                            (when (= 0 row-idx) "top-0 z-30 border border-b-1 border-b-black"))})
      [:span
       {:class "flex flex-col gap-1"}
       [:div {:class "truncate"} question-label]
       [:div {:class "text-xs font-thin"} question-ext-id]]])
   question-label])

(defn popover-body
  [{:keys [answer-in-excel error error-not-resolved?]}]
  [:div
   {:class "flex flex-col gap-1"}
   [:div
    [:span
     {:class "flex gap-2"}
     [:p (loc/tr :survey-import-value-in-excel)]
     [:p
      {:class "font-bold"}
      answer-in-excel]]]
   [:p
    {:class (uu/cm "text-secondary-light-text-accent max-w-[250px] text-sm"
                   (when error-not-resolved? "text-danger"))}
    (loc/tr (first error) (second error))]])

(defn resolve-errors-table-row
  [{:keys [survey-id row-idx row-values question-label question-id
           question-ext-id questions-lookup number-format lang]}]
  (into [:tr
         {:class (uu/cm "h-[60px]"
                        (when (= 0 row-idx)
                          "sticky top-0 z-20 bg-white"))}]
        (concat [[question-label-cell
                  {:row-idx row-idx
                   :question-label question-label
                   :question-ext-id question-ext-id}]]
                (->> row-values
                     (map-indexed (fn [col-idx val]
                                    (let [answer-in-excel (:original-value val)
                                          resolved-answer (:resolved-value val)
                                          cell-id (:cell-id val)
                                          error (:error val)
                                          question-config (get questions-lookup question-id)
                                          {:keys [survey-question/data-type survey-question/options]} question-config
                                          resolved-options (resolve-cell-options
                                                            {:options options
                                                             :resolved-answer resolved-answer
                                                             :data-type data-type
                                                             :lang lang})
                                          resolved-value (resolve-cell-value
                                                          {:data-type data-type
                                                           :resolved-options resolved-options
                                                           :resolved-answer resolved-answer})
                                          resolved-error? (= :resolved (first error))
                                          error-not-resolved? (and error (not-any? #{(first error)} [:resolved]))
                                          cell-in-edit-mode? @(rf/subscribe [::survey-view-sub/editing-error-cell? survey-id cell-id])
                                          show-error-cell? (and error-not-resolved? (not cell-in-edit-mode?))
                                          select? (some #{data-type} [:data.type/ref-single
                                                                      :data.type/ref-multi
                                                                      :data.type/keyword
                                                                      :data.type/keyword-multi])]
                                      (concat
                                       [^{:key (str col-idx "-resolved-answer")}
                                        [:td
                                         {:class (uu/cm "p-2 max-w-[120px] border border-[#D9D9DA] border-r-black
                                                         last:border-r-[#D9D9DA] min-w-[120px]"
                                                        (when error-not-resolved? "bg-[#FEE2E2]")
                                                        (when resolved-error? "bg-[#D1FAE5]")
                                                        (when (= 0 row-idx) "border border-b-1 border-b-black")
                                                        (when (= data-type :data.type/boolean) "text-left align-middle"))}
                                         [popover/view
                                          {:open? cell-in-edit-mode?
                                           :on-open-auto-focus (fn [e] (dg/prevent-and-stop e))
                                           :direction :top}
                                          (if show-error-cell?
                                            [error-cell
                                             {:survey-id survey-id
                                              :cell-id cell-id
                                              :answer-in-excel answer-in-excel}]
                                            [data-input/view
                                             {:editor-type   (case data-type
                                                               :data.type/boolean :editor.type/checkbox
                                                               nil)
                                              :data-type     (case data-type
                                                               :data.type/boolean :data.type/keyword
                                                               data-type)
                                              :number-format number-format
                                              :class          (uu/cm "w-full px-[0.5rem] pr-[0.5rem] py-2.5 text-left border
                                                                      border-transparent outline-none focus:ring-1 focus:ring-[#38363B]"
                                                                     (when (and (not select?) error-not-resolved?) "bg-[#FEE2E2] text-[#991B1B]")
                                                                     (when (and (not select?) resolved-error?) "bg-[#D1FAE5] text-[#065F46]"))
                                              :variant       :borderless
                                              :full-width?   true
                                              :show-clear?   true
                                              :transparent?  true
                                              :auto-focus? cell-in-edit-mode?
                                              :placeholder   (if (some #{data-type} [:data.type/ref-single
                                                                                     :data.type/ref-multi
                                                                                     :data.type/keyword
                                                                                     :data.type/keyword-multi])
                                                               (loc/tr :select-placeholder nil lang)
                                                               (loc/tr data-type nil lang))
                                              :value         resolved-value
                                              :options       (->> resolved-options
                                                                  (map (fn [opt]
                                                                         (assoc opt :value (pr-str (:value opt))))))
                                              :on-focus (fn []
                                                          (when error
                                                            (rf/dispatch [::survey-view-event/start-error-cell-edit survey-id cell-id])))
                                              :on-change     (fn [new-value]
                                                               (rf/dispatch [::survey-view-event/on-error-resolve-input survey-id new-value question-id cell-id]))
                                              :on-blur (fn []
                                                         (when error
                                                           (rf/dispatch [::survey-view-event/end-error-cell-edit survey-id cell-id question-id])))}])
                                          [popover-body
                                           {:answer-in-excel answer-in-excel
                                            :error error
                                            :error-not-resolved? error-not-resolved?}]]]]))))
                     (vec)
                     (doall)))))

(defn resolve-errors-table-body
  [{:keys [survey-id filtered-parsed-data questions-lookup number-format lang]}]
  (into
   [:tbody]
   (->> filtered-parsed-data
        (sort-by (comp :question-index first second))
        (into (sorted-map))
        (map-indexed (fn [row-idx [question-id row-values]]
                       (let [question-label (->> (get questions-lookup question-id)
                                                 (:survey-question/label-translations)
                                                 lang)
                             question-ext-id (->> row-values
                                                  (remove nil?)
                                                  (first)
                                                  :question-external-id)]
                         ^{:key (str (count row-values) "-" row-idx)}
                         [resolve-errors-table-row
                          {:survey-id survey-id
                           :row-idx row-idx
                           :row-values row-values
                           :question-label question-label
                           :question-id question-id
                           :question-ext-id question-ext-id
                           :questions-lookup questions-lookup
                           :number-format number-format
                           :lang question-ext-id}]))))))

(defn resolve-errors-modal-body
  [{:keys [survey-id filtered-parsed-data parsed-data lang unresolved-errors? unresolved-error-count
           resolved-errors? resolved-error-count]}]
  (let [[questions _question-sections] @(rf/subscribe [::survey-view-sub/survey-questions survey-id])
        number-format @(rf/subscribe [::survey-view-sub/num-fmt])
        show-warning? @(rf/subscribe [::sub/show-resolve-errors-warning?])
        show-rows-without-errors? @(rf/subscribe [::sub/show-rows-without-errors?])
        show-columns-without-errors? @(rf/subscribe [::sub/show-columns-without-errors?])
        single-column? @(rf/subscribe [::survey-view-sub/data-has-one-column? survey-id])
        questions-lookup (->> questions
                              (map (fn [q]
                                     [(:survey-question-id q) q]))
                              (into {}))]
    [:div
     {:class "h-full justify-between mx-4 flex flex-col gap-4"}
     [:div
      {:class "h-full flex flex-col gap-6"}
      (when show-warning?
        [alert/warning
         {:on-close (fn []
                      (rf/dispatch [::event/hide-resolve-errors-warning]))}
         [:p
          {:class "font-semibold text-md leading-6 mr-3 mb-2"}
          (loc/tr :survey-import-resolve-errors-warning-1)]
         [:p {:class "font-semibold text-md leading-6 mr-3"}
          (loc/tr :survey-import-resolve-errors-warning-2)]])
      [:div
       {:class "flex flex-row gap-4"}
       [:div
        {:class "inline-flex justify-between items-center gap-3"}
        [toggle/toggle
         {:checked? show-rows-without-errors?
          :on-change (fn [new-value]
                       (rf/dispatch [::event/toggle-show-rows-without-errors new-value]))}]
        [:label
         (loc/tr :survey-import-show-rows)]]
       (when-not single-column?
         [:div
          {:class "inline-flex justify-between items-center gap-3"}
          [toggle/toggle
           {:checked? show-columns-without-errors?
            :on-change (fn [new-value]
                         (rf/dispatch [::event/toggle-show-columns-without-errors new-value]))}]
          [:label
           (loc/tr :survey-import-show-columns)]])
       [button/sm-secondary-solid
        {:on-click (fn []
                     (rf/dispatch [::survey-view-event/refresh-resolved-errors survey-id parsed-data]))
         :disabled? (not resolved-errors?)}
        (str (loc/tr :survey-import-hide-resolved-errors)  "(" resolved-error-count ")")]
       [button/sm-secondary-solid
        {:on-click (fn []
                     (rf/dispatch [::survey-view-event/clear-all-cells-with-errors survey-id parsed-data]))
         :disabled? (not unresolved-errors?)}
        (str (loc/tr :survey-import-clear-cells) "(" unresolved-error-count ")")]]
      [:div
       {:class "w-full h-full overflow-x-auto overflow-y-auto border border-[#D9D9DA]"}
       [:table
        {:class "w-full border-separate border-spacing-0"}
        [resolve-errors-table-body
         {:survey-id survey-id
          :filtered-parsed-data filtered-parsed-data
          :questions-lookup questions-lookup
          :number-format number-format
          :lang lang}]]]]]))

(defn resolve-errors-modal-footer
  [{:keys [survey-id parsed-data _lang unresolved-errors?]}]
  [:div
   {:class "flex flex-row flex-auto"}
   [:div
    {:class "flex flex-row justify-content-start"}
    [button/secondary-hollow
     {:on-click (fn [_]
                  (rf/dispatch [::survey-view-event/close-resolving-errors-modal survey-id]))}
     (loc/tr :cancel)]]
   [:div
    {:class "flex flex-row flex-auto content-stretch justify-content-end"}
    [button/primary-solid
     {:on-click (fn [_]
                  (rf/dispatch [::survey-view-event/submit-tesi-esg-survey-data survey-id parsed-data]))
      :disabled? unresolved-errors?
      :tooltip (when unresolved-errors? (loc/tr :survey-import-finish-tooltip))}
     (loc/tr :survey-import-finish)]]])

(defn view
  []
  (let [survey-id @(rf/subscribe [::sub/survey-id])
        lang @(rf/subscribe [::survey-view-sub/selected-lang survey-id])
        parsed-data @(rf/subscribe [::survey-view-sub/unconfirmed-parsed-data-by-survey survey-id])
        filtered-parsed-data @(rf/subscribe [::survey-view-sub/filtered-unconfirmed-parsed-data survey-id])
        unresolved-errors? @(rf/subscribe [::survey-view-sub/unresolved-errors? survey-id])
        resolved-errors? @(rf/subscribe [::survey-view-sub/resolved-errors? survey-id])
        unresolved-error-count @(rf/subscribe [::survey-view-sub/unresolved-error-count survey-id])
        resolved-error-count @(rf/subscribe [::survey-view-sub/resolved-error-count survey-id])]
    [modal/view
     {:visible? @(rf/subscribe [::survey-view-sub/resolving-errors? survey-id])
      :hide!    #(rf/dispatch [::survey-view-event/close-resolving-errors-modal survey-id])
      :size     ::modal-style/full}
     (loc/tr :survey-import-resolve-errors)
     [resolve-errors-modal-body {:survey-id survey-id
                                 :filtered-parsed-data filtered-parsed-data
                                 :parsed-data parsed-data
                                 :lang lang
                                 :unresolved-errors? unresolved-errors?
                                 :resolved-errors? resolved-errors?
                                 :unresolved-error-count unresolved-error-count
                                 :resolved-error-count resolved-error-count}]
     [resolve-errors-modal-footer {:survey-id survey-id
                                   :parsed-data parsed-data
                                   :lang lang
                                   :unresolved-errors? unresolved-errors?}]]))
